import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterEvent } from '@angular/router';

import {
  NEVER,
  Observable,
  catchError,
  combineLatest,
  defer,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  race,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  throwError,
  timer,
  zip
} from 'rxjs';

import { CommonRoutes, RouteData } from '@hdms/shared/common-types';
import {
  CookieConsentStatusService,
  EnvironmentService,
  FT_LOGIN_TRACKING_CONSENT,
  FeatureToggleService,
  PlatformService,
  PreloadType,
  PreloaderService,
  RedirectService,
  RouterHelperService,
  SERVER_REQUEST,
  TokenService,
  WindowRefService,
  b64EncodeUnicode,
  generateQueryParamString,
  removeEmptyObjectProps,
  sanitizeUrl
} from '@hdms/shared/util-core';

import { FT_NGC_TOKEN_SERVICE_WORKER } from '../toggles';
import { UserContextService } from '../user';

import { ContextIds, LogoutDetailsResponse, OauthUserType, RequestParam, StateParam } from './oauth.types';
import {
  LOGIN_URLS,
  LOGOUT_DATA_URL,
  LOGOUT_REDIRECT_URL,
  LOGOUT_STATE_PLACEHOLDER,
  LOGOUT_TOKEN_PLACEHOLDER,
  REFRESH_URLS,
  SILENT_LOGIN_URLS,
  SWITCH_ACCOUNT_URL
} from './oauth.urls';

const IGNORED_ROUTES = [CommonRoutes.EMPTY, CommonRoutes.GENERIC_ERROR].map(r => `/${r}`);

interface RefreshAuthResponse {
  token: string;
}

/**
 * Take first NavigationEnd, which is the final one on server side
 * @param event
 */
function isFirstNavigationEnd(event: RouterEvent): boolean {
  return event.id === 1 && event instanceof NavigationEnd;
}

/**
 * Take either initial (faked) NavigationStart or subsequent ones,
 * which are no internal redirects to error/empty route
 * @param event
 */
function isValidNavigationStart(event: RouterEvent): boolean {
  return event instanceof NavigationStart && (event.id === null || !IGNORED_ROUTES.includes(event.url));
}

function base64Encode(data: StateParam): string {
  return b64EncodeUnicode(JSON.stringify(data));
}

@Injectable({
  providedIn: 'root'
})
export class OauthService {
  private readonly request = inject(SERVER_REQUEST, { optional: true });
  private readonly envService = inject(EnvironmentService);
  private readonly userContext = inject(UserContextService);
  private readonly platform = inject(PlatformService);
  private readonly router = inject(Router);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly httpClient = inject(HttpClient);
  private readonly tokenService = inject(TokenService);
  private readonly ftService = inject(FeatureToggleService);
  private readonly win = inject<WindowRefService>(WindowRefService).nativeWindow;
  private readonly analyticsConsent = inject(CookieConsentStatusService);
  private readonly redirect = inject(RedirectService);
  private readonly preloader = inject(PreloaderService);

  private readonly routeData$ = this.getRouteData$();
  private readonly state$ = this.createState$();
  private readonly refreshUrls = this.ftService.getToggleById(FT_NGC_TOKEN_SERVICE_WORKER)
    ? REFRESH_URLS
    : SILENT_LOGIN_URLS;

  private userType: OauthUserType;

  constructor() {
    this.setUserType();
  }

  /**
   * Returns login url with up-to-date base64 encoded state param
   */
  public getLoginUrl$(type = this.userType): Observable<string> {
    const { customerId, contactId } = this.getContextIds(type);
    const url = LOGIN_URLS.get(type);

    if (this.ftService.getToggleById(FT_LOGIN_TRACKING_CONSENT)) {
      return combineLatest([this.state$, this.analyticsConsent.update$]).pipe(
        map(([state, consent]) => this.generateUrlWithParams(url, state, customerId, contactId, consent))
      );
    } else {
      return this.state$.pipe(map(state => this.generateUrlWithParams(url, state, customerId, contactId)));
    }
  }

  /**
   * Return logout url plus parameters to be submitted via form.
   */
  public getLogoutDetails$(): Observable<LogoutDetailsResponse> {
    return this.getLogoutState$().pipe(
      switchMap(state => this.fetchLogoutDetails$(state)),
      take(1)
    );
  }

  /**
   * Return switch-account url containing provided customer originId
   * @param originId
   */
  public getAccountSwitchUrl$(originId: string): Observable<string> {
    return this.state$.pipe(map(state => this.generateUrlWithParams(SWITCH_ACCOUNT_URL, state, originId)));
  }

  /**
   * Invoke silent token refresh request.
   * In case refreshUrl is unset or token refresh request fails,
   * a redirect to cloud login will be triggered.
   * If redirecting is not possible (e.g. due to redirect loop prevention),
   * an error will be thrown.
   * @public
   */
  public refreshAuthToken$(): Observable<string> {
    const refreshUrl = this.getRefreshUrl();

    const redirectOnError$ = (err: Error): Observable<never> =>
      this.getLoginUrl$().pipe(
        switchMap(url => {
          if (this.redirect.redirectTo(url, true, true)) {
            this.preloader.startLoading(PreloadType.MANUAL);
            return NEVER;
          } else {
            return throwError(() => err);
          }
        })
      );

    return refreshUrl
      ? this.httpClient
          .get<RefreshAuthResponse>(refreshUrl, {
            withCredentials: true
          })
          .pipe(
            map(({ token }) => token),
            tap(token => {
              this.tokenService.bearerToken = token;
            }),
            catchError(err => redirectOnError$(err))
          )
      : redirectOnError$(new Error('refresh-url unset'));
  }

  /**
   * Returns refresh url with current customer originId.
   * In case logged in as account manager or eVoucher backoffice user,
   * use a dedicated url. For AM also add contact originId.
   * Returned url will be null in case customer originId is unset.
   */
  private getRefreshUrl(): string {
    const { customerId, contactId } = this.getContextIds();
    const { isEVoucherBackofficeUser, isPendingRegistration } = this.userContext;

    return customerId || isEVoucherBackofficeUser || isPendingRegistration
      ? this.generateUrlWithParams(
          this.refreshUrls.get(this.userType),
          this.getRefreshUrlState(),
          customerId,
          contactId
        )
      : null;
  }

  private getRefreshUrlState(): string {
    return this.envService.oauthClientRedirectHost ? this.createStateParam('') : null;
  }

  /**
   * Returns logout state:
   * In case route data contains `ssoLogoutRedirectRoute`, the value
   * will be applied to `state` for redirecting after logout.
   * In all other cases, `state` will be set to the current location.
   */
  private getLogoutState$(): Observable<string> {
    return this.routeData$.pipe(
      map(
        ({ ssoRedirectRoute, ssoLogoutRedirectRoute }) =>
          ssoRedirectRoute || ssoLogoutRedirectRoute || this.getLocation()
      ),
      map(redirectUrl => this.createStateParam(LOGOUT_REDIRECT_URL + redirectUrl))
    );
  }

  /**
   * Fetches Logout details from OauthClient and replaces
   * the placeholders in parameters with actual values.
   */
  private fetchLogoutDetails$(state: string): Observable<LogoutDetailsResponse> {
    return this.httpClient.get<LogoutDetailsResponse>(LOGOUT_DATA_URL).pipe(
      map(data => {
        const { parameters } = data;
        Object.keys(parameters).forEach(key => {
          switch (parameters[key]) {
            case LOGOUT_TOKEN_PLACEHOLDER:
              parameters[key] = this.tokenService.bearerToken;
              break;
            case LOGOUT_STATE_PLACEHOLDER:
              parameters[key] = state;
          }
        });
        return data;
      })
    );
  }

  /**
   * Create state observable returning a base64-encoded state object.
   * State has to be regenerated on every route change to match with actual browser location.
   * As encoding is expensive the observable is created once and replays latest value.
   * The state object will already be generated on NavigationStart, to get the target url
   * before potential route guards would invoke a redirect.
   * In case of late router events subscriptions, defer observable creation
   * to ensure `startWith` will take the most recent location.
   * In case `ssoRedirectRoute` is configured in `RouteData`,
   * this value will have precedence over current route url.
   * @private
   */
  private createState$(): Observable<string> {
    return zip(
      defer(() =>
        this.router.events.pipe(
          startWith(new NavigationStart(null, this.getLocation())),
          filter<RouterEvent>((event: RouterEvent) => isFirstNavigationEnd(event) || isValidNavigationStart(event)),
          map(({ url }: RouterEvent) => (this.platform.isBrowser ? url : this.getLocation())),
          distinctUntilChanged()
        )
      ),
      // Guards waiting for GraphQL responses can block the NavigationEnd event to happen.
      // In this case nothing else is possible as to skip routeData$ after some waiting time.
      race(this.routeData$.pipe(map(({ ssoRedirectRoute }) => ssoRedirectRoute)), timer(100))
    ).pipe(
      map(([routeUrl, redirectUrl]) => this.createStateParam(redirectUrl || routeUrl || '/')),
      shareReplay(1)
    );
  }

  private getRouteData$(): Observable<RouteData> {
    return this.routerHelper.activatedRoute$.pipe(mergeMap(({ data }) => data));
  }

  private generateUrlWithParams(
    url: string,
    state?: string,
    customerId?: string,
    contactId?: string,
    analyticsConsent: boolean = null
  ): string {
    return `${url}?${this.generateParams(state, customerId, contactId, analyticsConsent)}`;
  }

  private generateParams(
    state?: string,
    customerId?: string,
    contactId?: string,
    analyticsConsent: boolean = null
  ): string {
    return generateQueryParamString({
      [RequestParam.STATE]: state,
      [RequestParam.CUSTOMER_ID]: customerId,
      [RequestParam.CONTACT_ID]: contactId,
      [RequestParam.COUNTRY]: this.envService.country,
      [RequestParam.LANG]: this.envService.locale,
      [RequestParam.ANALYTICS_CONSENT]: analyticsConsent
    });
  }

  /**
   * Return current pathname including query params and fragment
   * @private
   */
  private getLocation(): string {
    const { href, origin } = this.win.location;
    return href.replace(origin, '');
  }

  private setUserType(): void {
    this.userType = this.getUserType();
  }

  private getUserType(): OauthUserType {
    const { isAccountManager, isEVoucherBackofficeUser } = this.userContext;

    switch (true) {
      case isAccountManager:
        return OauthUserType.AM;
      case isEVoucherBackofficeUser:
        return OauthUserType.EVOUCHER;
      default:
        return OauthUserType.DEFAULT;
    }
  }

  private getContextIds(type = this.userType): ContextIds {
    const {
      customerOriginId: customerId,
      contactOriginId: contactId,
      preselectedShipToId: shipToId
    } = this.userContext;

    switch (type) {
      case OauthUserType.AM:
        return { customerId, contactId, shipToId };
      case OauthUserType.EVOUCHER:
        return {};
      default:
        return { customerId };
    }
  }

  /**
   * Create state param object, always containing country and redirect url.
   * @param url
   * @private
   */
  private createStateParam(url: string): string {
    const { queryParams: query, fragment } = this.router.parseUrl(url);

    return base64Encode(
      removeEmptyObjectProps<StateParam>({
        targetUrl: {
          countryCode: this.envService.country,
          relativeUrl: sanitizeUrl(url),
          redirectHost: this.getRedirectHost(),
          query,
          fragment
        }
      })
    );
  }

  /**
   * return current hostname as redirect host in case related
   * env variable is configured on local or staging envs.
   * @private
   */
  private getRedirectHost(): string {
    return this.envService.oauthClientRedirectHost && (this.request?.hostname || this.win.location.hostname);
  }
}
